<template>
  <wizzard-container :infos="infos">
    <div class="title">{{ $t('Business Form Basic Info') }}</div>
    <p class="caption">{{ $t('Business Form Basic Info Caption') }}</p>

    <v-text-field
      v-model="form.place.name"
      :label="$t('Name')"
      outlined
      dense
      :error-messages="firstError('form.place.name')"
    />

    <v-textarea
      v-model="form.content.description[$i18n.locale]"
      :error-messages="firstError(`form.content.description.${$i18n.locale}`)"
      class="mt-2"
      outlined
      counter="2000"
      :label="$t('Form Info Description')"
      :placeholder="$t('Form Info Description')"
      dense
    >
      <template slot="prepend">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :color="showTranslatable ? 'primary' : 'grey'"
              dark
              v-bind="attrs"
              v-on="on"
              @click="toggleTranslations"
            >
              mdi-translate
            </v-icon>
          </template>
          <span v-if="showTranslatable">
            {{ $t('Hide other locales') }}
          </span>
          <span v-else>
            {{ $t('Show other locales') }}
          </span>
        </v-tooltip>
      </template>
    </v-textarea>
    <template v-if="showTranslatable">
      <v-textarea
        v-for="locale in otherLocales"
        :key="`desc_${locale}`"
        v-model="form.content.description[locale]"
        :error-messages="firstError(`form.content.description.${locale}`)"
        class="mt-2"
        outlined
        counter="2000"
        :label="`${$t('Form Info Description')} ${locale}`"
        :placeholder="`${$t('Form Info Description')} ${locale}`"
        dense
      />
    </template>

    <v-textarea
      v-model="form.content.description_short[$i18n.locale]"
      :error-messages="firstError(`form.content.description_short.${$i18n.locale}`)"
      class="mt-2"
      outlined
      counter="2000"
      :label="$t('Form Info Short Description')"
      :placeholder="$t('Form Info Short Description')"
      dense
    >
      <template slot="prepend">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :color="showTranslatableShort ? 'primary' : 'grey'"
              dark
              v-bind="attrs"
              v-on="on"
              @click="toggleTranslationsShort"
            >
              mdi-translate
            </v-icon>
          </template>
          <span v-if="showTranslatableShort">
            {{ $t('Hide other locales') }}
          </span>
          <span v-else>
            {{ $t('Show other locales') }}
          </span>
        </v-tooltip>
      </template>
    </v-textarea>
    <template v-if="showTranslatableShort">
      <v-textarea
        v-for="locale in otherLocales"
        :key="`short_desc_${locale}`"
        v-model="form.content.description_short[locale]"
        :error-messages="firstError(`form.content.description_short.${locale}`)"
        class="mt-2"
        outlined
        counter="2000"
        :label="`${$t('Form Info Short Description')} ${locale}`"
        :placeholder="`${$t('Form Info Short Description')} ${locale}`"
        dense
      />
    </template>

    <v-select
      v-model="form.place.place_type_id"
      :items="normalizedTypes"
      item-text="nameText"
      item-value="id"
      :label="$t('Business Form Type')"
      outlined
      dense
      :error-messages="firstError('form.place.place_type_id')"
    />
    <v-select
      v-model="form.place.place_type_ids"
      :items="normalizedTypes"
      item-text="nameText"
      item-value="id"
      :label="$t('Business Form Secondary Types')"
      multiple
      chips
      outlined
      :error-messages="firstError('form.place.place_type_ids')"
      class="mt-2"
    />
    <v-text-field
      v-model="form.links.websiteUri"
      :label="$t('Website')"
      outlined
      dense
      :error-messages="firstError('form.links.websiteUri')"
    />
    <vue-tel-input-vuetify
      outlined
      :preferred-countries="['rs', 'ba', 'me', 'hr', 'si', 'de', 'mk', 'ch']"
      :valid-characters-only="true"
      :error-messages="firstError('form.place.internationalPhoneNumber')"
      @input="onPhoneInput"
      :value="form.place.internationalPhoneNumber ? form.place.internationalPhoneNumber : ''"
      :hint="phone.number.international"
      persistent-hint
      :label="$t('Phone')"
    />
    <v-select
      v-model="form.place.businessStatus"
      :items="translatedBusinessStatusOptions"
      item-text="label"
      item-value="value"
      :label="$t('Business status')"
      outlined
      dense
      :error-messages="firstError('form.place.businessStatus')"
      class="mt-2"
    />

    <div class="mt-4">
      <div class="d-flex align-center justify-space-between mb-2">
        <div class="subtitle-1">{{ $t('Key features') }}</div>
        <v-btn
          small
          outlined
          color="primary"
          :disabled="keyFeaturesLimitReached"
          @click="addKeyFeature"
        >
          + {{ $t('Add') }}
        </v-btn>
      </div>
      <div
        v-for="(feature, index) in form.content.key_features"
        :key="`key_feature_${index}`"
        class="mb-3"
      >
        <v-textarea
          v-model="feature[$i18n.locale]"
          outlined
          dense
          rows="2"
          :counter="maxKeyFeatureLength"
          :label="`${$t('Key features')} #${index + 1}`"
          :error-messages="getKeyFeatureFieldErrors(index, $i18n.locale)"
          @blur="$v.form.content.key_features.$each[index][$i18n.locale].$touch()"
        >
          <template slot="prepend">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  :color="isKeyFeatureTranslationsShown(index) ? 'primary' : 'grey'"
                  dark
                  v-bind="attrs"
                  v-on="on"
                  @click="toggleTranslationsKeyFeatures(index)"
                >
                  mdi-translate
                </v-icon>
              </template>
              <span v-if="isKeyFeatureTranslationsShown(index)">
                {{ $t('Hide other locales') }}
              </span>
              <span v-else>
                {{ $t('Show other locales') }}
              </span>
            </v-tooltip>
          </template>
        </v-textarea>
        <template v-if="isKeyFeatureTranslationsShown(index)">
          <v-textarea
            v-for="locale in otherLocales"
            :key="`key_feature_${index}_${locale}`"
            v-model="feature[locale]"
            outlined
            dense
            rows="2"
            :counter="maxKeyFeatureLength"
            :label="`${$t('Key features')} ${locale} #${index + 1}`"
            :error-messages="getKeyFeatureFieldErrors(index, locale)"
            @blur="$v.form.content.key_features.$each[index][locale].$touch()"
          />
        </template>
        <div class="d-flex justify-end">
          <v-btn
            small
            text
            color="error"
            @click="removeKeyFeature(index)"
          >
            {{ $t('Delete') }}
          </v-btn>
        </div>
      </div>
      <div v-if="keyFeaturesGeneralErrors.length" class="error--text text-caption mb-2">
        {{ keyFeaturesGeneralErrors[0] }}
      </div>
    </div>
    <form-action-buttons
      status="listed"
      @submit="submit"
    />
  </wizzard-container>
</template>

<script>
import axios from 'axios';
import { validationMixin } from 'vuelidate';
import {
  maxLength,
  minLength,
  required,
  url,
} from 'vuelidate/lib/validators';
import FormValidationMixin from '@/lib/FormValidationMixin';
import FormActionButtons from '@/views/ads/forms/FormActionButtons.vue';
import WizzardContainer from '@/views/ads/forms/WizzardContainer.vue';

export default {
  mixins: [validationMixin, FormValidationMixin],
  components: {
    FormActionButtons,
    WizzardContainer,
  },
  props: {
    businessId: { type: String, required: true },
    business: { type: Object, default: null },
    placeTypes: { type: Array, required: true },
    businessStatusOptions: { type: Array, default: () => [] },
  },
  validations() {
    return {
      form: {
        place: {
          name: {
            required,
            maxLength: maxLength(255),
          },
          place_type_id: {
            required,
          },
          place_type_ids: {
            required,
          },
          businessStatus: {
            required,
          },
          internationalPhoneNumber: {
            required,
            maxLength: maxLength(100),
          },
        },
        content: {
          description: {
            en: {
              required,
              minLength: minLength(10),
            },
            sr: {
              required,
              minLength: minLength(10),
            },
          },
          description_short: {
            en: {
              required,
              minLength: minLength(10),
            },
            sr: {
              required,
              minLength: minLength(10),
            },
          },
          key_features: {
            keyFeaturesMaxCount: (value) => !value || value.length <= this.maxKeyFeatures,
            $each: {
              en: {
                required,
                maxLength: maxLength(this.maxKeyFeatureLength),
              },
              sr: {
                required,
                maxLength: maxLength(this.maxKeyFeatureLength),
              },
            },
          },
        },
        links: {
          websiteUri: {
            url,
          },
        },
      },
    };
  },
  data() {
    return {
      form: {
        place: {
          name: null,
          place_type_id: null,
          place_type_ids: [],
          businessStatus: null,
          internationalPhoneNumber: null,
        },
        content: {
          description: {
            en: '',
            sr: '',
          },
          description_short: {
            en: '',
            sr: '',
          },
          key_features: [],
        },
        links: {
          websiteUri: '',
        },
      },
      phone: {
        number: {
          international: '',
          e164: '',
        },
      },
      showTranslatable: false,
      showTranslatableShort: false,
      showTranslatableKeyFeatures: {},
      maxKeyFeatures: 10,
      maxKeyFeatureLength: 120,
    };
  },
  watch: {
    business: {
      immediate: true,
      handler() {
        this.setFormData();
      },
    },
    'form.place.place_type_id': function (value) {
      if (!value) {
        return;
      }

      if (!this.form.place.place_type_ids.includes(value)) {
        this.form.place.place_type_ids = [
          ...this.form.place.place_type_ids,
          value,
        ];
      }
    },
  },
  computed: {
    normalizedTypes() {
      return this.placeTypes.map((type) => ({
        ...type,
        nameText: (type.name && (type.name[this.$i18n.locale] || type.name.en)) || type.const,
      }));
    },
    translatedBusinessStatusOptions() {
      return this.businessStatusOptions.map((option) => ({
        ...option,
        label: this.$t(option.label),
      }));
    },
    otherLocales() {
      return this.$i18n.availableLocales.filter((locale) => locale !== this.$i18n.locale);
    },
    keyFeaturesLimitReached() {
      return this.form.content.key_features.length >= this.maxKeyFeatures;
    },
    keyFeaturesGeneralErrors() {
      const errors = [];

      if (
        this.$v.form.content.key_features
        && !this.$v.form.content.key_features.keyFeaturesMaxCount
      ) {
        errors.push(this.$t('validation.maxLength', { n: this.maxKeyFeatures }));
      }

      const backendError = this.errorsBackend
        && this.errorsBackend['content.key_features']
        && this.errorsBackend['content.key_features'][0]
        ? this.errorsBackend['content.key_features'][0]
        : undefined;
      if (backendError) {
        errors.push(backendError);
      }

      return errors;
    },
    infos() {
      return [
        {
          icon: 'green-energy',
          title: 'Business Form Basic Info InfoTitle',
          description: 'Business Form Basic Info InfoDescription',
        },
      ];
    },
  },
  methods: {
    hasLocaleValidationError(path, locale) {
      const field = path
        .split('.')
        .reduce((obj, key) => (obj && obj[key] ? obj[key] : null), this.$v);

      return Boolean(field && field[locale] && field[locale].$invalid);
    },
    hasLocaleBackendError(path, locale) {
      const fieldPath = `${path}.${locale}`;

      return Boolean(
        this.errorsBackend
        && this.errorsBackend[fieldPath]
        && this.errorsBackend[fieldPath][0],
      );
    },
    revealHiddenTranslationErrors() {
      const hasDescriptionError = this.otherLocales.some((locale) => (
        this.hasLocaleValidationError('form.content.description', locale)
        || this.hasLocaleBackendError('content.description', locale)
      ));

      const hasDescriptionShortError = this.otherLocales.some((locale) => (
        this.hasLocaleValidationError('form.content.description_short', locale)
        || this.hasLocaleBackendError('content.description_short', locale)
      ));

      if (hasDescriptionError) {
        this.showTranslatable = true;
      }

      if (hasDescriptionShortError) {
        this.showTranslatableShort = true;
      }

      this.form.content.key_features.forEach((feature, index) => {
        const hasHiddenValidationError = this.otherLocales.some((locale) => (
          this.$v.form.content.key_features.$each[index][locale]
          && this.$v.form.content.key_features.$each[index][locale].$invalid
        ));
        const hasHiddenBackendError = this.otherLocales.some((locale) => (
          this.errorsBackend
          && this.errorsBackend[`content.key_features.${index}.${locale}`]
          && this.errorsBackend[`content.key_features.${index}.${locale}`][0]
        ));

        if (hasHiddenValidationError || hasHiddenBackendError) {
          this.$set(this.showTranslatableKeyFeatures, index, true);
        }
      });
    },
    setFormData() {
      if (!this.business) {
        return;
      }

      this.form.place = {
        ...this.form.place,
        ...this.business,
      };
      this.form.place.businessStatus = this.form.place.businessStatus || 'BUSINESS_STATUS_UNSPECIFIED';

      const businessTypeIds = Array.isArray(this.business.types)
        ? this.business.types.map((type) => type.id).filter(Boolean)
        : [];

      if (businessTypeIds.length) {
        this.form.place.place_type_ids = businessTypeIds;
      } else if (this.form.place.place_type_id) {
        this.form.place.place_type_ids = [this.form.place.place_type_id];
      } else {
        this.form.place.place_type_ids = [];
      }

      const description = this.business.content ? this.business.content.description : null;
      const descriptionShort = this.business.content ? this.business.content.description_short : null;

      this.form.content.description = {
        en: (description && description.en) || '',
        sr: (description && description.sr) || '',
      };
      this.form.content.description_short = {
        en: (descriptionShort && descriptionShort.en) || '',
        sr: (descriptionShort && descriptionShort.sr) || '',
      };
      this.form.content.key_features = Array.isArray(this.business.content && this.business.content.key_features)
        ? this.business.content.key_features.map((feature) => ({
          en: (feature && feature.en) || '',
          sr: (feature && feature.sr) || '',
        }))
        : [];
      this.showTranslatableKeyFeatures = {};

      this.form.links = {
        ...this.form.links,
        ...(this.business.links || {}),
      };
    },
    addKeyFeature() {
      if (this.keyFeaturesLimitReached) {
        return;
      }

      this.form.content.key_features.push({
        en: '',
        sr: '',
      });
    },
    removeKeyFeature(index) {
      this.form.content.key_features.splice(index, 1);
      const nextVisibility = {};
      this.form.content.key_features.forEach((feature, featureIndex) => {
        nextVisibility[featureIndex] = Boolean(
          this.showTranslatableKeyFeatures[featureIndex >= index ? (featureIndex + 1) : featureIndex],
        );
      });
      this.showTranslatableKeyFeatures = nextVisibility;
    },
    isKeyFeatureTranslationsShown(index) {
      return Boolean(this.showTranslatableKeyFeatures[index]);
    },
    getKeyFeatureFieldErrors(index, locale) {
      const errors = [];

      if (this.$v.form.content.key_features.$each[index][locale].$dirty) {
        if (!this.$v.form.content.key_features.$each[index][locale].required) {
          errors.push(this.$t('validation.required'));
        }

        if (!this.$v.form.content.key_features.$each[index][locale].maxLength) {
          errors.push(this.$t('validation.maxLength', { n: this.maxKeyFeatureLength }));
        }
      }

      const backendErrorKey = `content.key_features.${index}.${locale}`;
      const backendError = this.errorsBackend
        && this.errorsBackend[backendErrorKey]
        && this.errorsBackend[backendErrorKey][0]
        ? this.errorsBackend[backendErrorKey][0]
        : undefined;
      if (backendError) {
        errors.push(backendError);
      }

      return errors;
    },
    onPhoneInput(formattedNumber, phone) {
      this.phone = phone;
      this.form.place.internationalPhoneNumber = phone && phone.number ? phone.number.e164 : '';
    },
    toggleTranslations() {
      this.showTranslatable = !this.showTranslatable;
    },
    toggleTranslationsShort() {
      this.showTranslatableShort = !this.showTranslatableShort;
    },
    toggleTranslationsKeyFeatures(index) {
      this.$set(
        this.showTranslatableKeyFeatures,
        index,
        !this.showTranslatableKeyFeatures[index],
      );
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.revealHiddenTranslationErrors();
        this.scrollToFirstError();
        return;
      }
      this.$store.dispatch('loading', true);
      axios
        .post(`panel/ads/validation/business/basic-info/${this.businessId}`, this.form)
        .then(() => {
          this.$store.dispatch('clearErrors');
          this.$store.dispatch('message', this.$t('Saved successfully'));
          this.$emit('success');
        })
        .catch((error) => {
          const errors = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors
            : {};
          this.$store.dispatch('setErrors', errors);
          this.revealHiddenTranslationErrors();
          this.scrollToFirstError();
        })
        .finally(() => {
          this.$store.dispatch('loading', false);
        });
    },
  },
};
</script>
